import React, { useEffect, useMemo, useRef, useState } from 'react';
import MobileSelect from '../components/MobileSelect.tsx';
import MobileDatePicker from '../components/MobileDatePicker.tsx';
import MobileFileUpload from '../components/MobileFileUpload.tsx';
import { scheduleAPI, lookupAPI, profileAPI } from '../services/api.ts';
import { toast } from 'react-toastify';
import { useConfirm } from '../context/ConfirmContext.tsx';

type LeaveType = 'Annual' | 'Sick' | 'Unpaid' | 'Emergency' | 'Other';

interface LeaveRequest {
	id: string;
	leaveType: LeaveType;
	startDate: string;
	endDate: string;
	duration: 'full' | 'half-am' | 'half-pm';
	reason: string;
	status: 'Pending' | 'Approved' | 'Rejected';
}

interface SwipeableLeaveItemProps {
  item: any;
  idx: number;
  deletingLeaveId: number | null;
  downloadingAttachmentKey: string | null;
  onEdit: (item: any) => void;
  onDelete: (leaveId: number) => void;
  onDownload: (leaveId: number, fileMeta: any, overrideName?: string) => void;
}

const BRAND_RED = '#D92D30';

const SwipeableLeaveItem: React.FC<SwipeableLeaveItemProps> = ({
  item,
  idx,
  deletingLeaveId,
  downloadingAttachmentKey,
  onEdit,
  onDelete,
  onDownload
}) => {
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const ACTION_WIDTH = 140;

  const parseApiDate = (value: any) => {
    if (value === null || value === undefined || value === '') return null;
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const start = parseApiDate(item.StartDate ?? item.startDate);
  const end = parseApiDate(item.EndDate ?? item.endDate);
  const status = item.Status || item.status;
  const wf = item.WorkFlowStatusId ?? item.workFlowStatusId;
  const type = item.LeaveType || item.leaveType;
  const remarks = item.Remarks || item.remarks;
  const manager = item.ManagerRemark || item.managerRemark;
  const leaveId = item.Id ?? item.id ?? item.LeaveId ?? item.leaveId;
  const files =
    Array.isArray(item.files) ? item.files :
    Array.isArray(item.Files) ? item.Files :
    Array.isArray(item.Attachments) ? item.Attachments :
    [];
  const leaveIdNumber = Number(leaveId);
  const fmt = (d: Date | null) => d ? d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
  const statusClass = (wf === 2 || /approved/i.test(status))
    ? 'bg-green-100 text-green-700'
    : (wf === 3 || /reject/i.test(status))
      ? ''
      : 'bg-yellow-100 text-yellow-700';
  const leftBorder = (wf === 2 || /approved/i.test(status)) ? 'border-green-500' : (wf === 3 || /reject/i.test(status)) ? '' : 'border-yellow-400';
  const leaveCount = (item.LeaveCount ?? item.leaveCount);
  const canEdit = (() => {
    if (!leaveIdNumber) return false;
    if (wf === 0 || /pending/i.test(status)) return true;
    if ((wf === 2 || /approved/i.test(status)) && start && start.getTime() > Date.now()) return true;
    return false;
  })();

  const handleTouchStart = (e: React.TouchEvent) => {
    if (!canEdit) return;
    setTouchStartX(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!canEdit || touchStartX === null) return;
    const currentX = e.touches[0].clientX;
    const diff = touchStartX - currentX;
    if (diff > 0) setSwipeOffset(Math.min(diff, ACTION_WIDTH));
    else setSwipeOffset(0);
  };

  const handleTouchEnd = () => {
    if (!canEdit) return;
    setSwipeOffset(swipeOffset > ACTION_WIDTH / 2 ? ACTION_WIDTH : 0);
    setTouchStartX(null);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (itemRef.current && !itemRef.current.contains(e.target as Node)) {
        setSwipeOffset(0);
      }
    };
    if (swipeOffset > 0) document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [swipeOffset]);

  return (
    <div
      ref={itemRef}
      className={`relative overflow-hidden rounded-lg shadow-sm border border-gray-100 mb-2 border-l-4 ${leftBorder} ${idx === 0 ? 'mt-2' : ''}`}
      style={(wf === 3 || /reject/i.test(status)) ? { borderLeftColor: BRAND_RED } : undefined}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      style={{ touchAction: 'pan-y' }}
    >
      <div
        className="bg-white p-3 transition-transform duration-200 ease-out"
        style={{ transform: `translateX(-${swipeOffset}px)` }}
      >
        <div className="flex items-start justify-between">
          <div className="font-medium pr-2 truncate">{type}</div>
          <div className="flex items-center gap-2">
            {leaveCount !== undefined && (
              <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700">{leaveCount} day{Number(leaveCount) === 1 ? '' : 's'}</span>
            )}
            <span
              className={`text-xs px-2 py-1 rounded-full ${statusClass}`}
              style={(wf === 3 || /reject/i.test(status))
                ? { backgroundColor: '#fdebec', color: BRAND_RED }
                : undefined}
            >
              {status}
            </span>
          </div>
        </div>
        <div className="text-xs text-gray-600 mt-1">{fmt(start)} — {fmt(end ?? start)}</div>
        {remarks && <div className="text-sm text-gray-700 mt-1">{remarks}</div>}
        {manager && <div className="text-xs text-gray-500 mt-1">Manager: {manager}</div>}
        {files.length > 0 && (
          <div className="mt-2 space-y-1">
            {files.map((fileMeta: any, fileIdx: number) => {
              const rawName =
                fileMeta?.FileName ??
                fileMeta?.fileName ??
                fileMeta?.Name ??
                fileMeta?.name ??
                fileMeta?.OriginalName ??
                fileMeta?.originalName ??
                fileMeta?.DisplayName ??
                fileMeta?.displayName ??
                `Attachment-${fileIdx + 1}`;
              const displayName = String(rawName || `Attachment-${fileIdx + 1}`).trim();
              const downloadKey = `${leaveIdNumber}-${displayName}`;
              const isDownloading = downloadingAttachmentKey === downloadKey;
              return (
                <button
                  key={downloadKey}
                  type="button"
                  onClick={() => onDownload(leaveIdNumber, fileMeta, displayName)}
                  className="inline-flex items-center gap-1 text-xs text-primary underline"
                  disabled={isDownloading}
                >
                  {isDownloading ? 'Downloading...' : displayName}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {canEdit && (
        <div
          className="absolute right-0 top-0 bottom-0 flex items-center"
          style={{ width: `${ACTION_WIDTH}px`, transform: `translateX(${ACTION_WIDTH - swipeOffset}px)` }}
        >
          <button
            onClick={() => {
              onEdit(item);
              setSwipeOffset(0);
            }}
            className="w-[70px] h-full bg-blue-500 text-white flex items-center justify-center text-xs font-medium"
          >
            Edit
          </button>
          <button
            onClick={() => {
              onDelete(leaveIdNumber);
              setSwipeOffset(0);
            }}
            disabled={deletingLeaveId === leaveIdNumber}
            className="w-[70px] h-full text-white flex items-center justify-center text-xs font-medium disabled:opacity-70"
            style={{ backgroundColor: BRAND_RED }}
          >
            {deletingLeaveId === leaveIdNumber ? '...' : 'Delete'}
          </button>
        </div>
      )}
    </div>
  );
};

const Leave: React.FC = () => {
	const [showForm, setShowForm] = useState(false);
	const [leaveType, setLeaveType] = useState<LeaveType>('Annual');
	const [leaveTypeId, setLeaveTypeId] = useState<string>('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
  const [openPicker, setOpenPicker] = useState<'start' | 'end' | null>(null);
	const [reason, setReason] = useState('');
	const [requests, setRequests] = useState<LeaveRequest[]>([]);
	const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');
	const [duration, setDuration] = useState<'full' | 'half-am' | 'half-pm'>('full');
	const [validationError, setValidationError] = useState<string | null>(null);
  const [historyUpcoming, setHistoryUpcoming] = useState<any[]>([]);
  const [historyPast, setHistoryPast] = useState<any[]>([]);
  const [leaveTypeOptions, setLeaveTypeOptions] = useState<{ value: string; label: string }[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [editingLeaveId, setEditingLeaveId] = useState<number | null>(null);
  const [downloadingAttachmentKey, setDownloadingAttachmentKey] = useState<string | null>(null);
  const [deletingLeaveId, setDeletingLeaveId] = useState<number | null>(null);
  const { confirm } = useConfirm();

  // Helpers to safely format/parse dates in local timezone (avoid UTC shifting)
  const formatLocalDate = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const parseLocalDate = (str: string) => {
    const [y, m, d] = str.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  };

	// Mock balances summary card data (replace with API later)
	const [balances, setBalances] = useState<{ key: string; available: number; taken: number; note?: string; }[]>([]);

	useEffect(() => {
		// Fetch leave types from API and map to display
    scheduleAPI.getLeaveTypes()
        .then((rows: any[]) => {
            const mapped = (rows || []).map(r => ({
                key: r.LeaveType || r.leaveType || 'Leave',
                available: Number((r.Balance ?? r.balance) ?? 0),
                taken: Number((r.LeaveCount ?? r.leaveCount) ?? 0)
            }));
            setBalances(mapped);
        })
			.catch(() => {
				setBalances([]);
			});

    // Fetch master leave types for selection
    lookupAPI.getLeaveTypes()
      .then((rows) => {
        const opts = (rows || []).map((r: any) => ({ value: String(r.id), label: r.name }));
        setLeaveTypeOptions(opts);
        // default selection if not set
        if (!leaveType && opts.length > 0) setLeaveType(opts[0].label as LeaveType);
        if (!leaveTypeId && opts.length > 0) setLeaveTypeId(opts[0].value);
      })
      .catch(() => setLeaveTypeOptions([]));

		// Fetch leave history
		scheduleAPI.getLeaveHistory()
			.then((res: any) => {
				setHistoryUpcoming(res.pendingRejected || res.pendingRejected || []);
				setHistoryPast(res.approved || res.approved || []);
			})
			.catch(() => {
				setHistoryUpcoming([]);
				setHistoryPast([]);
			});
	}, []);

	const canSubmit = useMemo(() => {
		if (!leaveTypeId || !startDate) return false;
		if (duration === 'full' && !endDate) return false;
		if (duration !== 'full' && startDate !== endDate) return false;
		return true;
	}, [leaveTypeId, startDate, endDate, duration]);

	const resetForm = () => {
		setLeaveType('Annual');
		setLeaveTypeId('');
		setStartDate('');
		setEndDate('');
		setReason('');
		setSelectedFile(null);
		setDuration('full');
		setValidationError(null);
    setEditingLeaveId(null);
	};

  const loadLeaveIntoForm = (item: any) => {
    const leaveId = Number(item?.Id ?? item?.id ?? item?.LeaveId ?? item?.leaveId ?? 0);
    if (!leaveId) return;

    const normalizeDateString = (value: any) => {
      if (!value) return '';
      const date = new Date(value);
      return isNaN(date.getTime()) ? '' : formatLocalDate(date);
    };

    const normalizedStart = normalizeDateString(item.StartDate ?? item.startDate);
    const normalizedEnd = normalizeDateString(item.EndDate ?? item.endDate ?? item.StartDate ?? item.startDate);
    const halfDay = Boolean(item.HalfDay ?? item.halfDay ?? item.IsHalfDay ?? item.isHalfDay ?? false);
    const sessionId = item.LeaveSessionId ?? item.leaveSessionId ?? item.LeaveSession ?? item.leaveSession;

    setEditingLeaveId(leaveId);
    setShowForm(true);

    const leaveTypeValue = item.LeaveTypeId ?? item.leaveTypeId ?? item.LeaveTypeID ?? item.leaveTypeID;
    if (leaveTypeValue !== undefined && leaveTypeValue !== null) {
      setLeaveTypeId(String(leaveTypeValue));
      const option = leaveTypeOptions.find(o => o.value === String(leaveTypeValue));
      if (option) {
        setLeaveType(option.label as LeaveType);
      }
    } else if (item.LeaveType || item.leaveType) {
      const option = leaveTypeOptions.find(o => o.label === (item.LeaveType || item.leaveType));
      if (option) {
        setLeaveTypeId(option.value);
        setLeaveType(option.label as LeaveType);
      } else {
        setLeaveType(item.LeaveType || item.leaveType || 'Annual');
      }
    }

    setStartDate(normalizedStart);
    setEndDate(normalizedEnd);

    if (halfDay) {
      if (sessionId === 1) {
        setDuration('half-am');
      } else if (sessionId === 2) {
        setDuration('half-pm');
      } else {
        setDuration('half-am');
      }
    } else {
      setDuration('full');
    }

    setReason(item.Remarks ?? item.remarks ?? '');
    setSelectedFile(null);
    setValidationError(null);
  };

  const handleDownloadAttachment = async (leaveId: number, fileMeta: any, overrideName?: string) => {
    const rawName =
      overrideName ??
      fileMeta?.FileName ??
      fileMeta?.fileName ??
      fileMeta?.Name ??
      fileMeta?.name ??
      fileMeta?.OriginalName ??
      fileMeta?.originalName ??
      '';
    const fileName = String(rawName || '').trim();
    if (!fileName) {
      toast.error('Unable to determine file name.');
      return;
    }
    const key = `${leaveId}-${fileName}`;
    setDownloadingAttachmentKey(key);
    try {
      const blob = await profileAPI.downloadLeaveDocument(leaveId, fileName);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('Error downloading leave attachment:', error);
      toast.error(error?.message || 'Failed to download attachment.');
    } finally {
      setDownloadingAttachmentKey(null);
    }
  };

  const handleDeleteLeave = async (leaveId: number) => {
    if (!leaveId) return;
    const confirmDelete = await confirm('Are you sure you want to delete this leave request?');
    if (!confirmDelete) return;

    setDeletingLeaveId(leaveId);
    try {
      await profileAPI.deleteLeaveApplication(leaveId);
      toast.success('Leave request deleted successfully');

      const res: any = await scheduleAPI.getLeaveHistory();
      setHistoryUpcoming(res.pendingRejected || res.pendingRejected || []);
      setHistoryPast(res.approved || res.approved || []);
    } catch (error: any) {
      console.error('Error deleting leave request:', error);
      toast.error(error?.message || 'Failed to delete leave request');
    } finally {
      setDeletingLeaveId(null);
    }
  };

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		// Validate half-day rule: only allowed when start === end
		if (duration !== 'full' && startDate !== endDate) {
			setValidationError('Half-day is only allowed when Start Date equals End Date.');
			return;
		}
		setValidationError(null);
		if (!canSubmit) return;
		try {
			setSubmitting(true);
      const leaveTypeValue = Number(leaveTypeId || (leaveTypeOptions.find(o => o.label === leaveType)?.value || 0));
      const payload = {
        LeaveTypeId: leaveTypeValue,
        LeaveStartDate: startDate,
        LeaveEndDate: duration === 'full' ? endDate : undefined,
        HalfDay: duration !== 'full',
        LeaveSessionId: duration === 'half-am' ? 1 : duration === 'half-pm' ? 2 : undefined,
        Remarks: reason,
        FileItem: selectedFile || undefined
      };

      if (editingLeaveId) {
        await profileAPI.updateLeaveApplication({
          Id: editingLeaveId,
          ...payload
        });
      } else {
        await profileAPI.leaveApplication(payload);
      }
			// Refresh history after successful apply
			const res: any = await scheduleAPI.getLeaveHistory();
      setHistoryUpcoming(res.pendingRejected || res.pendingRejected || []);
      setHistoryPast(res.approved || res.approved || []);
			setShowForm(false);
			resetForm();
		} catch (err: any) {
			// surface backend message when available
			setValidationError(String(err?.message || err) || 'Failed to submit leave. Please try again.');
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<div className="px-4 py-4">
			<section className="mb-4">
				<div className="flex items-center justify-between">
					<h2 className="text-lg font-semibold">Leave</h2>
					<button
						onClick={() => {
              if (editingLeaveId) {
                resetForm();
                setShowForm(false);
              } else {
                setShowForm(v => !v);
              }
            }}
						className="bg-primary text-white px-3 py-2 rounded"
					>
						{editingLeaveId ? 'Cancel Edit' : showForm ? 'Close' : 'Apply Leave'}
					</button>
				</div>
			</section>

			{/* Balances summary cards - attractive mini tiles */}
            <div className="grid grid-cols-3 gap-3 mb-4">
                {balances.map(card => {
                    const isNegative = card.available < 0;
                    const dotClass = isNegative ? '' : 'bg-emerald-500';
                    return (
                        <div key={card.key} className="bg-white rounded-xl shadow-sm p-3 border border-gray-100">
                            <div className="flex items-center justify-between mb-1">
                                <div className="text-sm font-medium truncate pr-2">{card.key}</div>
                                <span className={`w-2 h-2 rounded-full ${dotClass}`} style={isNegative ? { backgroundColor: BRAND_RED } : undefined}></span>
                            </div>
                            <div className={`text-2xl font-bold leading-none ${isNegative ? '' : 'text-gray-900'}`} style={isNegative ? { color: BRAND_RED } : undefined}>{card.available.toFixed(1)}</div>
                            <div className="text-[10px] text-gray-500 tracking-wide">AVAILABLE</div>
                            <div className={`text-[10px] mt-1 ${isNegative ? '' : 'text-gray-500'}`} style={isNegative ? { color: BRAND_RED } : undefined}>{card.taken} days taken</div>
                        </div>
                    );
                })}
				{balances.length === 0 && (
					<div className="col-span-3 text-sm text-gray-500">No balances found.</div>
				)}
			</div>

			{showForm && (
				<form onSubmit={handleSubmit} className="bg-white rounded border p-3 mb-4 space-y-3">
					<div>
					<MobileSelect
						label="Leave Type"
						value={leaveTypeId || ''}
						options={leaveTypeOptions.length ? leaveTypeOptions : [
							{ value: 'Annual', label: 'Annual' },
							{ value: 'Sick', label: 'Sick' },
							{ value: 'Unpaid', label: 'Unpaid' },
							{ value: 'Emergency', label: 'Emergency' },
							{ value: 'Other', label: 'Other' },
						]}
						onChange={(val) => {
							setLeaveTypeId(val);
							const selected = leaveTypeOptions.find(o => o.value === val);
							if (selected) setLeaveType(selected.label as LeaveType);
						}}
						placeholder="Select leave type"
					/>
					<MobileFileUpload label="Attachment (optional)" file={selectedFile} onChange={setSelectedFile} />
					</div>
					<div className="grid grid-cols-2 gap-3">
						<div>
							<label className="block text-sm text-gray-700 mb-1">Start Date</label>
							<button onClick={() => setOpenPicker('start')} className="w-full border rounded px-2 py-2 text-left">
                {startDate || 'Select date'}
              </button>
						</div>
						<div>
							<label className="block text-sm text-gray-700 mb-1">End Date</label>
							<button onClick={() => setOpenPicker('end')} className="w-full border rounded px-2 py-2 text-left">
                {endDate || 'Select date'}
              </button>
						</div>
					</div>

					{/* Duration selector */}
					<div className="mt-3">
						<div className="block text-sm text-gray-700 mb-1">Duration</div>
						<div className="grid grid-cols-3 gap-2">
							<button type="button" onClick={() => setDuration('full')} className={`px-2 py-2 rounded border text-sm ${duration==='full' ? 'bg-primary text-white border-primary' : 'bg-white'}`}>Full Day</button>
							<button type="button" onClick={() => setDuration('half-am')} className={`px-2 py-2 rounded border text-sm ${duration==='half-am' ? 'bg-primary text-white border-primary' : 'bg-white'}`}>Half Day AM</button>
							<button type="button" onClick={() => setDuration('half-pm')} className={`px-2 py-2 rounded border text-sm ${duration==='half-pm' ? 'bg-primary text-white border-primary' : 'bg-white'}`}>Half Day PM</button>
						</div>
						{duration !== 'full' && startDate && endDate && startDate !== endDate && (
							<div className="text-xs mt-1" style={{ color: BRAND_RED }}>Half-day requires Start Date equals End Date.</div>
						)}
					</div>
					<div>
						<label className="block text-sm text-gray-700 mb-1">Reason (optional)</label>
						<textarea
							value={reason}
							onChange={e => setReason(e.target.value)}
							rows={3}
							className="w-full border rounded px-2 py-2"
						/>
					</div>
					{validationError && <div className="text-xs" style={{ color: BRAND_RED }}>{validationError}</div>}
					<div className="flex justify-end gap-2">
						<button
              type="button"
              className="px-3 py-2 rounded border"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
            >
              Cancel
            </button>
						<button type="submit" disabled={!canSubmit || submitting} className={`px-3 py-2 rounded text-white ${canSubmit && !submitting ? 'bg-primary' : 'bg-gray-300 cursor-not-allowed'}`}>{submitting ? 'Submitting...' : editingLeaveId ? 'Update' : 'Submit'}</button>
					</div>
				</form>
			)}

      {/* Mobile date pickers */}
      <MobileDatePicker
        isOpen={openPicker === 'start'}
        onClose={() => setOpenPicker(null)}
        selectedDate={startDate ? parseLocalDate(startDate) : null}
        onSelectDate={(d) => { setStartDate(d ? formatLocalDate(d) : ''); setOpenPicker(null); }}
      />
      <MobileDatePicker
        isOpen={openPicker === 'end'}
        onClose={() => setOpenPicker(null)}
        selectedDate={endDate ? parseLocalDate(endDate) : null}
        onSelectDate={(d) => { setEndDate(d ? formatLocalDate(d) : ''); setOpenPicker(null); }}
      />

			{/* Tabs: Upcoming/Pending vs Past/Canceled */}
				<div className="bg-white rounded border mb-3">
				<div className="flex text-sm">
					<button onClick={() => setTab('upcoming')} className={`flex-1 py-2 ${tab === 'upcoming' ? 'text-primary border-b-2 border-primary font-medium' : 'text-gray-500'}`}>Pending/Rejected</button>
					<button onClick={() => setTab('past')} className={`flex-1 py-2 ${tab === 'past' ? 'text-primary border-b-2 border-primary font-medium' : 'text-gray-500'}`}>Approved</button>
				</div>
					<div className="divide-y">
						{(tab === 'upcoming' ? historyUpcoming : historyPast).length === 0 ? (
							<div className="p-3 text-sm text-gray-500">No requests yet.</div>
						) : (
                        (tab === 'upcoming' ? historyUpcoming : historyPast).map((item: any, idx: number) => (
                          <SwipeableLeaveItem
                            key={`${item?.Id ?? item?.id ?? idx}`}
                            item={item}
                            idx={idx}
                            deletingLeaveId={deletingLeaveId}
                            downloadingAttachmentKey={downloadingAttachmentKey}
                            onEdit={loadLeaveIntoForm}
                            onDelete={handleDeleteLeave}
                            onDownload={handleDownloadAttachment}
                          />
                        ))
						)}
					</div>
			</div>
		</div>
	);
};

export default Leave;


